import React from 'react'
import { useTheme, Typography, Box } from '@mui/material'
import {
    FiberManualRecord,
    Apartment,
    AttachFile,
    ChatBubbleOutline,
    Event,
} from '@mui/icons-material'

import { FlexDiv } from '../FlexDiv'
import { TenantMessage } from '../../types/tenantMessage'
import { getShortFormDate } from '../../helpers/stringHelpers'

interface TenantMessageCardProps {
    message: TenantMessage
    onClick: () => void
}

export const TenantMessageCard: React.FC<TenantMessageCardProps> = ({
    message,
    onClick,
}) => {
    const theme = useTheme()

    const preview =
        message.body.length > 100
            ? `${message.body.substring(0, 100)}...`
            : message.body

    return (
        <Box
            onClick={onClick}
            sx={{
                background:
                    'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                borderRadius: 3,
                padding: 3,
                marginBottom: 2,
                border: `1px solid ${theme.palette.grey[700]}`,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'border-color 0.2s ease',
                '&:hover': {
                    borderColor: theme.palette.primary.main,
                },
            }}
        >
            {!message.is_read && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #3b82f6 100%)`,
                    }}
                />
            )}

            <FlexDiv
                style={{
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: theme.spacing(1),
                }}
            >
                <FlexDiv style={{ alignItems: 'center', flex: 1, minWidth: 0 }}>
                    {!message.is_read && (
                        <FiberManualRecord
                            sx={{
                                color: theme.palette.primary.main,
                                fontSize: 12,
                                marginRight: theme.spacing(1),
                                flexShrink: 0,
                            }}
                        />
                    )}
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: '#fff',
                            fontWeight: message.is_read ? 400 : 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {message.subject}
                    </Typography>
                </FlexDiv>

                <Typography
                    variant="caption"
                    sx={{
                        color: '#8b949e',
                        flexShrink: 0,
                        marginLeft: theme.spacing(2),
                    }}
                >
                    {getShortFormDate(
                        message.latest_reply_at || message.created_date,
                    )}
                </Typography>
            </FlexDiv>

            <FlexDiv
                style={{
                    alignItems: 'center',
                    marginBottom: theme.spacing(1),
                }}
            >
                <Apartment
                    sx={{
                        color: '#8b949e',
                        fontSize: 16,
                        marginRight: theme.spacing(0.5),
                    }}
                />
                <Typography variant="body2" sx={{ color: '#8b949e' }}>
                    {message.apartment_name}
                </Typography>
                {message.attachments.length > 0 && (
                    <FlexDiv
                        style={{
                            alignItems: 'center',
                            marginLeft: theme.spacing(1.5),
                        }}
                    >
                        <AttachFile
                            sx={{
                                color: '#8b949e',
                                fontSize: 14,
                                marginRight: theme.spacing(0.25),
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{ color: '#8b949e' }}
                        >
                            {message.attachments.length}
                        </Typography>
                    </FlexDiv>
                )}
                {message.property_event_id && (
                    <FlexDiv
                        style={{
                            alignItems: 'center',
                            marginLeft: theme.spacing(1.5),
                        }}
                    >
                        <Event
                            sx={{
                                color: theme.palette.primary.main,
                                fontSize: 14,
                                marginRight: theme.spacing(0.25),
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{ color: theme.palette.primary.main }}
                        >
                            Event
                        </Typography>
                    </FlexDiv>
                )}
                {message.reply_count > 0 && (
                    <FlexDiv
                        style={{
                            alignItems: 'center',
                            marginLeft: theme.spacing(1.5),
                        }}
                    >
                        <ChatBubbleOutline
                            sx={{
                                color: '#8b949e',
                                fontSize: 14,
                                marginRight: theme.spacing(0.5),
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{ color: '#8b949e' }}
                        >
                            {message.reply_count}
                        </Typography>
                    </FlexDiv>
                )}
            </FlexDiv>

            {message.initiated_by_tenant && (
                <Typography
                    variant="caption"
                    sx={{
                        color: theme.palette.primary.main,
                        fontStyle: 'italic',
                        marginBottom: theme.spacing(0.5),
                        display: 'block',
                    }}
                >
                    You started this conversation
                </Typography>
            )}

            <Typography
                variant="body2"
                sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    lineHeight: 1.5,
                }}
            >
                {preview}
            </Typography>
        </Box>
    )
}
